import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { attendanceService } from '../services/attendance';
import { useAuth } from './AuthContext';
import { startOfMonth } from 'date-fns';
import type { AttendanceRecord, MonthlySummary } from '../types';

interface AttendanceContextValue {
  todayStatus: AttendanceRecord | null;
  loading: boolean;
  history: AttendanceRecord[];
  monthlySummary: MonthlySummary | null;
  checkIn: (photoDataUrl?: string) => Promise<AttendanceRecord>;
  checkOut: (photoDataUrl?: string) => Promise<AttendanceRecord>;
  /** Full refresh of all loaded data (for the Dashboard refresh button). */
  refresh: () => Promise<void>;
  /** Lazy-load history — skips if already loaded unless force=true. */
  loadHistory: (force?: boolean) => Promise<void>;
  /** Lazy-load monthly summary — skips if already loaded unless force=true. */
  loadMonthlySummary: (force?: boolean) => Promise<void>;
}

const AttendanceContext = createContext<AttendanceContextValue | null>(null);

export const useAttendance = (): AttendanceContextValue => {
  const context = useContext(AttendanceContext);
  if (!context) throw new Error('useAttendance must be used within AttendanceProvider');
  return context;
};

export const AttendanceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [todayStatus, setTodayStatus] = useState<AttendanceRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<AttendanceRecord[]>([]);
  const [monthlySummary, setMonthlySummary] = useState<MonthlySummary | null>(null);

  // Ref-based guards: prevent double-fetching without adding to useCallback deps.
  const historyLoadedRef = useRef(false);
  const summaryLoadedRef = useRef(false);

  // On logout: clear state and reset loaded flags.
  useEffect(() => {
    if (!isAuthenticated) {
      setTodayStatus(null);
      setHistory([]);
      setMonthlySummary(null);
      historyLoadedRef.current = false;
      summaryLoadedRef.current = false;
    }
  }, [isAuthenticated]);

  const loadTodayStatus = useCallback(async (): Promise<void> => {
    try {
      const status = await attendanceService.getTodayStatus();
      setTodayStatus(status);
    } catch (error) {
      console.error('Error loading today status:', error);
    }
  }, []);

  // Only auto-fetch today's status on auth — it's small and needed by Dashboard immediately.
  useEffect(() => {
    if (isAuthenticated) {
      void loadTodayStatus();
    }
  }, [isAuthenticated, loadTodayStatus]);

  /**
   * Lazy-loads attendance history for the current month.
   * Skips the network call if already loaded (unless force=true).
   * Pages call this in their own useEffect — only Dashboard, Attendance, and Profile need it.
   */
  const loadHistory = useCallback(async (force = false): Promise<void> => {
    if (!isAuthenticated) return;
    if (historyLoadedRef.current && !force) return;
    try {
      const records = await attendanceService.getAttendanceHistory(startOfMonth(new Date()), new Date());
      setHistory(records);
      historyLoadedRef.current = true;
    } catch (error) {
      console.error('Error loading history:', error);
    }
  }, [isAuthenticated]);

  /**
   * Lazy-loads the monthly attendance summary.
   * Skips the network call if already loaded (unless force=true).
   */
  const loadMonthlySummary = useCallback(async (force = false): Promise<void> => {
    if (!isAuthenticated) return;
    if (summaryLoadedRef.current && !force) return;
    try {
      const summary = await attendanceService.getMonthlySummary();
      setMonthlySummary(summary);
      summaryLoadedRef.current = true;
    } catch (error) {
      console.error('Error loading monthly summary:', error);
    }
  }, [isAuthenticated]);

  const checkIn = async (photoDataUrl?: string): Promise<AttendanceRecord> => {
    setLoading(true);
    try {
      const record = await attendanceService.checkIn(photoDataUrl);
      setTodayStatus(record);
      // Force-refresh so stats reflect the new check-in immediately.
      await loadHistory(true);
      await loadMonthlySummary(true);
      return record;
    } finally {
      setLoading(false);
    }
  };

  const checkOut = async (photoDataUrl?: string): Promise<AttendanceRecord> => {
    setLoading(true);
    try {
      const record = await attendanceService.checkOut(photoDataUrl);
      setTodayStatus(record);
      await loadHistory(true);
      await loadMonthlySummary(true);
      return record;
    } finally {
      setLoading(false);
    }
  };

  /** Full refresh for the Dashboard Refresh button. Force-reloads all loaded slices. */
  const refresh = async (): Promise<void> => {
    await loadTodayStatus();
    await loadHistory(true);
    await loadMonthlySummary(true);
  };

  const value: AttendanceContextValue = {
    todayStatus,
    loading,
    history,
    monthlySummary,
    checkIn,
    checkOut,
    refresh,
    loadHistory,
    loadMonthlySummary,
  };

  return <AttendanceContext.Provider value={value}>{children}</AttendanceContext.Provider>;
};
